<script>
  import i18n from "./i18n/i18n";
  import { tourFunctions } from "../tour-functions";
  // Tooltips
  import Tooltip from "./Tooltip.svelte";
  let showingTooltip = false;
  const showTooltip = () => {
    showingTooltip = true;
  };
  const hideTooltip = () => {
    showingTooltip = false;
  };

  // audio.src is unable to store null so we must
  // duplicate it into src for null checks
  let src = null;
  let audio = new Audio();
  export let playing = true;
  let firstPlay = true;

  export let loop = false;
  audio.loop = loop;

  export let resumable = true;
  let audioProgress = {};

  // debug
  window.audioObj = audio;

  // stores audio currentTime for each audio file
  function trackAudioProgress() {
    audioProgress[audio.src] = audio.currentTime;
  }

  // track audio progress so we can resume it later
  if (resumable) {
    // any time the audio time changes, track it
    audio.addEventListener("timeupdate", (e) => {
      trackAudioProgress();
    });

    // any time the audio is ends, track it
    audio.addEventListener("ended", (e) => {
      trackAudioProgress();
    });
  }

  import UA from "ua-parser-js";
  const ua = new UA();
  let isMobile = ua.getResult().device["type"] == "mobile";

  const firstPlayFailed = () => {
    if (firstPlay) {
      // no audio can play until a click occurs
      // so display that audio is muted
      playing = false;
      addEventListener("click", handleFirstClick);
      addEventListener("touchstart", handleFirstClick);
    }
  };

  const playSceneAudio = () => {
    let scene = tourFunctions.currentScene.getScene();
    if (scene.audio) {
      // check that src isn't the same to avoid audio replay
      if (src !== scene.audio) {
        audio.src = src = scene.audio;

        // resume if needed
        if (resumable && audio.src in audioProgress) {
          audio.currentTime = audioProgress[audio.src];
        }
      }

      // user didn't mute, play audio
      if (playing) {
        if (audio.ended && !loop) {
          return;
        }
        audio
          .play()
          // first play success
          .then(() => {
            firstPlay = false;
          })
          // first play fail, setup click event
          .catch(firstPlayFailed);
      }
    } else {
      // scene doesn't have audio
      audio.src = src = null;

      // first scene doesn't have audio,
      // click event will occur regardless,
      //  so display as playing
      if (firstPlay) {
        playing = true;
      }
    }
  };

  const handleFirstClick = (e) => {
    // set play to true before next function call
    playing = true;
    // this will now success due to click event
    playSceneAudio();
    // remove click handler
    removeEventListener("click", handleFirstClick);
    removeEventListener("touchstart", handleFirstClick);
  };

  addEventListener("krpano:newpano", (e) => {
    if (e.detail.scene) {
      playSceneAudio();
    }
  });

  // init
  playSceneAudio();

  const togglePlay = () => {
    playing = !playing;
  };

  // make playing reactive so it can be set from other components
  $: if (src) {
    playing ? audio.play() : audio.pause();
  }

  const text = playing ? $i18n.t('tour.pauseAudio') : $i18n.t('tour.playAudio');
</script>

<!-- /*____________ Markup ____________*/ -->
<div class="container" on:mouseenter={showTooltip} on:mouseleave={hideTooltip}>
  <Tooltip
    text={text}
    {showingTooltip}
    position={"right"}
  />
  <button 
    class="audio" 
    on:click={togglePlay}
    aria-label={text}
    aria-pressed={playing}
  >
    {#if playing}
      <svg
        fill="#fff"
        width="25"
        height="25"
        version="1.1"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 490 385.3"
        role="img"
        aria-hidden="true"
      >
        <g>
          <path
            d="M392.8,0c-4.7,0.2-9.1,2.2-12.2,5.7c-3.2,3.5-4.8,8-4.5,12.7c0.2,4.7,2.3,9.1,5.8,12.2c47,43.3,73.1,101.5,73.1,162.1
            c0,60.6-26.1,118.8-73.1,162.1c-3.5,3.1-5.5,7.5-5.7,12.2c-0.2,4.7,1.4,9.2,4.6,12.6c3.2,3.4,7.6,5.5,12.2,5.6
            c4.7,0.2,9.2-1.5,12.6-4.7C459.4,331,490,263.4,490,192.7c0-70.7-30.6-138.4-84.4-187.9C402.2,1.5,397.5-0.2,392.8,0L392.8,0z"
          />
          <path
            d="M350,87.5c-4.7-0.1-9.2,1.8-12.5,5.1c-3.3,3.3-5.2,7.8-5.2,12.4c0,4.7,1.9,9.2,5.2,12.4c19.2,19.5,30,46.1,30,73.9
            c0,27.8-10.8,54.4-30,73.9v0c-4.4,4.5-6,10.9-4.4,17c1.7,6,6.4,10.7,12.5,12.3c6.1,1.6,12.5-0.2,16.9-4.7
            c25.7-26.2,40-61.5,40-98.4c0-36.9-14.3-72.3-40-98.4C359.2,89.5,354.7,87.5,350,87.5L350,87.5z"
          />
          <path
            d="M224.9,1.1c-8.9,1.8-17.4,6.2-24,13.1c-25.9,26.7-56,57.8-74.3,76.7c-7.5,7.7-17.2,11.9-27.2,12
            c-20.6,0.1-36.5,0.4-49.7,2.3c-13.2,1.8-24.9,5.3-34,13.8C6.7,127.5,3,139.3,1.4,151.1C-0.1,162.9,0,176,0,192.7
            c0,16.7-0.1,29.8,1.4,41.6c1.5,11.8,5.3,23.5,14.3,32.1s20.8,12,34,13.8c13.2,1.8,29,2.1,49.7,2.3c10.1,0.1,19.8,4.3,27.2,12
            c18.3,18.9,48.4,50,74.3,76.7c13.2,13.6,33.7,17.8,51,10.1h0h0c17.3-7.7,28-25.2,28-44.1V48.1c0-18.9-10.7-36.3-28-44.1l0,0h0
            C243.3,0.2,233.8-0.7,224.9,1.1L224.9,1.1z M237.8,36.1c4.1,1.8,7.2,6.4,7.2,12.1v289.1c0,5.6-3.2,10.2-7.2,12.1
            c-4.1,1.8-8.2,1.1-11.7-2.5c-25.9-26.7-56-57.9-74.3-76.7c-13.7-14.2-32.4-22.4-51.9-22.7v0h-0.1c-20.4-0.1-35.3-0.6-45.2-1.9
            c-9.9-1.4-13.6-3.4-14.8-4.6s-2.6-3.2-3.7-11.1c-1-7.9-1.1-20.4-1.1-37.1c0-16.7,0.1-29.1,1.1-37.1c1-7.9,2.4-10,3.7-11.1
            c1.2-1.1,4.9-3.3,14.8-4.6c9.9-1.4,24.8-1.8,45.2-1.9v0h0.1c19.6-0.3,38.2-8.5,52-22.7c18.3-18.9,48.4-50,74.3-76.7
            C229.5,35,233.7,34.3,237.8,36.1L237.8,36.1z"
          />
        </g>
      </svg>
    {:else}
      <svg
        fill="#fff"
        width="25"
        height="25"
        version="1.1"
        viewBox="0 0 490.2 384.9"
        xmlns="http://www.w3.org/2000/svg"
        role="img"
        aria-hidden="true"
      >
        <g>
          <path
            d="M332.3,104.8c-4.7,0-9.1,1.9-12.4,5.2c-3.3,3.3-5.1,7.8-5.1,12.5c0,4.7,1.9,9.1,5.3,12.4l57.6,57.6l-57.6,57.6
            c-3.4,3.3-5.3,7.7-5.4,12.4c0,4.7,1.8,9.2,5.1,12.5c3.3,3.3,7.8,5.2,12.5,5.1c4.7,0,9.2-2,12.4-5.4l57.6-57.6l57.6,57.6h0
            c3.3,3.4,7.7,5.3,12.4,5.4c4.7,0,9.2-1.8,12.5-5.1c3.3-3.3,5.2-7.8,5.1-12.5c0-4.7-2-9.2-5.4-12.4l-57.6-57.6l57.6-57.6
            c3.4-3.3,5.3-7.8,5.3-12.6c0-4.7-1.9-9.3-5.3-12.6c-3.4-3.3-8-5.1-12.7-4.9c-4.5,0.1-8.9,2-12,5.3l-57.6,57.6l-57.6-57.6h0
            C341.6,106.7,337,104.8,332.3,104.8L332.3,104.8z"
          />
          <path
            d="M224.9,0.9c-8.9,1.8-17.4,6.2-24,13.1c-25.9,26.7-56,57.8-74.3,76.7c-7.5,7.7-17.2,11.9-27.2,12
            c-20.6,0.1-36.5,0.4-49.7,2.3c-13.2,1.8-24.9,5.3-34,13.8C6.7,127.3,3,139,1.4,150.8C-0.1,162.6,0,175.8,0,192.4
            c0,16.7-0.1,29.8,1.4,41.6c1.5,11.8,5.3,23.5,14.3,32.1s20.8,12,34,13.8c13.2,1.8,29,2.1,49.7,2.3c10.1,0.1,19.8,4.3,27.2,12
            c18.3,18.9,48.4,50,74.3,76.7c13.2,13.6,33.7,17.8,51,10.1h0h0c17.3-7.7,28-25.2,28-44.1V47.9c0-18.9-10.7-36.3-28-44.1l0,0h0
            C243.3,0,233.8-0.9,224.9,0.9L224.9,0.9z M237.8,35.8c4.1,1.8,7.2,6.4,7.2,12.1V337c0,5.6-3.2,10.2-7.2,12.1
            c-4.1,1.8-8.2,1.1-11.7-2.5c-25.9-26.7-56-57.9-74.3-76.7c-13.7-14.2-32.4-22.4-51.9-22.7v0h-0.1c-20.4-0.1-35.3-0.6-45.2-1.9
            c-9.9-1.4-13.6-3.4-14.8-4.6s-2.6-3.2-3.7-11.1c-1-7.9-1.1-20.4-1.1-37.1c0-16.7,0.1-29.1,1.1-37.1c1-7.9,2.4-10,3.7-11.1
            c1.2-1.1,4.9-3.3,14.8-4.6c9.9-1.4,24.8-1.8,45.2-1.9v0h0.1c19.6-0.3,38.2-8.5,52-22.7c18.3-18.9,48.4-50,74.3-76.7
            C229.5,34.8,233.7,34,237.8,35.8L237.8,35.8z"
          />
        </g>
      </svg>
    {/if}
  </button>
</div>

<!-- /*____________ CSS ____________*/ -->
<style>
  .container {
    position: fixed;
    bottom: 0;
    left: 0;
    z-index: 999;
    margin: 10px 20px;
  }

  /* place right on mobile */
  @media (max-width: 768px) {
    .container {
      right: 0;
      left: auto;
    }
  }

  .audio {
    /* background: rgba(var(--primary-color-rgb), 0.75); */
    background: transparent;
    border-radius: 10px;
    border-radius: 100%;
    padding: 5px;

    display: inline-flex;
    align-items: center;
    justify-content: center;

    cursor: pointer;
    border: 2px solid transparent;
  }

  .audio:hover {
    /* background: rgba(var(--primary-color-rgb), 1); */
    border: 2px solid #fff;
  }
</style>
